import {
  AlertTriangle,
  ArrowRight,
  Ban,
  CalendarX2,
  Clock3,
  ExternalLink,
  Link2Off,
  PlugZap,
  SearchX,
  ShieldAlert,
  UserCheck,
  UserPlus,
} from 'lucide-react'
import { SlowNotice } from '@pa/ui'
import type { ReactNode } from 'react'
import { BackButton, ScreenTitle, StateIcon } from './EntryPanel'

/**
 * The entry screens (UserFlows §2.3). Copy is UserFlows' literal text; markup is the mockup's `site/Widget.tsx`
 * `EntryPanel` views. `contact` is the contact block (full or compact), rendered by the caller.
 */

/** P-01 "Have you been seen at {Clinic} before?" */
export function ChooseScreen({
  clinicName,
  busy,
  onExisting,
  onNew,
}: {
  clinicName: string
  busy: boolean
  onExisting: () => void
  onNew: () => void
}) {
  return (
    <>
      <ScreenTitle className="wg-lead">Have you been seen at {clinicName} before?</ScreenTitle>
      <button type="button" className="wg-option" onClick={onExisting} disabled={busy} aria-busy={busy}>
        <span className="wo-ic" aria-hidden>
          <UserCheck />
        </span>
        <span className="grow">
          <b>I&apos;m an existing patient</b>
          <small>Book online in a couple of minutes</small>
        </span>
        {busy ? <span className="spinner" aria-hidden /> : <ArrowRight aria-hidden />}
      </button>
      <button type="button" className="wg-option" onClick={onNew} disabled={busy}>
        <span className="wo-ic alt" aria-hidden>
          <UserPlus />
        </span>
        <span className="grow">
          <b>I&apos;m a new patient</b>
          <small>Get in touch and we&apos;ll set you up</small>
        </span>
        <ArrowRight aria-hidden />
      </button>
    </>
  )
}

/** P-02 New patient: the full contact block; Back → P-01. */
export function NewPatientScreen({ onBack, contact }: { onBack: () => void; contact: ReactNode }) {
  return (
    <>
      <BackButton onClick={onBack} />
      <ScreenTitle>Welcome! Let&apos;s get you registered</ScreenTitle>
      <p className="wg-text">
        New patients book with our front desk, so we can set up your record first. Call or email us and we&apos;ll find
        you a time.
      </p>
      {contact}
    </>
  )
}

/** P-04 Record not found: "Try again" → P-03 with empty fields. */
export function NotFoundScreen({ onTryAgain, contact }: { onTryAgain: () => void; contact: ReactNode }) {
  return (
    <>
      <BackButton onClick={onTryAgain} label="Try again" />
      <StateIcon tone="warn">
        <SearchX />
      </StateIcon>
      <ScreenTitle>We couldn&apos;t find your record</ScreenTitle>
      <p className="wg-text">Please contact the clinic to continue booking. We&apos;re happy to help.</p>
      {contact}
    </>
  )
}

/** P-05 Lookup error: "Try again" → P-03 with the fields kept. */
export function LookupErrorScreen({ onTryAgain, contact }: { onTryAgain: () => void; contact: ReactNode }) {
  return (
    <>
      <BackButton onClick={onTryAgain} label="Try again" />
      <StateIcon tone="error">
        <AlertTriangle />
      </StateIcon>
      <ScreenTitle>Something went wrong</ScreenTitle>
      <p className="wg-text">
        We couldn&apos;t look up your record just now. Please try again in a few minutes, or contact us.
      </p>
      {contact}
    </>
  )
}

/** P-06 Online booking unavailable. No Back with `existing=1` or after lookup. */
export function UnavailableScreen({ onBack, contact }: { onBack: (() => void) | undefined; contact: ReactNode }) {
  return (
    <>
      {onBack && <BackButton onClick={onBack} />}
      <StateIcon tone="warn">
        <PlugZap />
      </StateIcon>
      <ScreenTitle>Online booking is unavailable right now</ScreenTitle>
      <p className="wg-text">
        We can&apos;t reach our scheduling system at the moment. Please contact us and we&apos;ll book you in.
      </p>
      {contact}
    </>
  )
}

/** P-14 Too many attempts (`blocked` / `rate_limited`). The real reason is never shown. */
export function BlockedScreen({ contact }: { contact: ReactNode }) {
  return (
    <>
      <StateIcon tone="error">
        <Ban />
      </StateIcon>
      <ScreenTitle>We couldn&apos;t continue your booking online</ScreenTitle>
      <p className="wg-text">Please call us and we&apos;ll book you in.</p>
      {contact}
    </>
  )
}

/**
 * P-15 Booking limit reached (task 17; D-12, D-33): `booking_cap_reached` straight after the lookup or at confirm.
 * The full contact block; no actions; the number and window are never shown.
 */
export function CapReachedScreen({ contact }: { contact: ReactNode }) {
  return (
    <>
      <StateIcon tone="info">
        <CalendarX2 />
      </StateIcon>
      <ScreenTitle>Please call the clinic to book more appointments</ScreenTitle>
      <p className="wg-text">
        You&apos;ve booked several appointments online recently. Our front desk can help with more.
      </p>
      {contact}
    </>
  )
}

/** P-16 Your session expired: Start again → P-01 (P-03 with `existing=1`). */
export function ExpiredScreen({ onStartAgain }: { onStartAgain: () => void }) {
  return (
    <>
      <StateIcon tone="info">
        <Clock3 />
      </StateIcon>
      <ScreenTitle>Your session expired</ScreenTitle>
      <p className="wg-text">For your security, please start again.</p>
      <button type="button" className="btn btn-primary btn-lg btn-block" onClick={onStartAgain}>
        Start again
      </button>
    </>
  )
}

/** P-17 Online booking isn't available (deactivated clinic, nothing bookable): the full contact block. */
export function UnavailableClinicScreen({ contact }: { contact: ReactNode }) {
  return (
    <>
      <StateIcon tone="warn">
        <PlugZap />
      </StateIcon>
      <ScreenTitle>Online booking isn&apos;t available right now</ScreenTitle>
      <p className="wg-text">Please contact us and we&apos;ll book you in.</p>
      {contact}
    </>
  )
}

/** P-18 copy (UserFlows P-18; task 22, D-79). */
export const P18_TITLE = "This booking link isn't available online right now"
export const P18_TEXT = "Please contact us and we'll book you in."

/**
 * P-18 This booking link isn't available online right now: the link's **locked** preset is no longer bookable online
 * (#6 `preset.locked && stale`). The full contact block, no actions (in the widget, close only).
 */
export function PresetUnavailableScreen({ contact }: { contact: ReactNode }) {
  return (
    <>
      <StateIcon tone="warn">
        <Link2Off />
      </StateIcon>
      <ScreenTitle>{P18_TITLE}</ScreenTitle>
      <p className="wg-text">{P18_TEXT}</p>
      {contact}
    </>
  )
}

/** Opening (config + session) or loading the options after a match. */
export function LoadingScreen() {
  return (
    <div className="wg-state">
      <span className="big-spinner" aria-hidden />
      <span className="sr-only" role="status">
        Loading
      </span>
      {/* Config (boot) and options are pending for as long as this shows (Frontend §4.8). */}
      <SlowNotice pending />
    </div>
  )
}

/**
 * The embed's cookies-blocked panel (task 08; Architecture §6.4, Frontend §4.4 step 9, §11 Q1 copy): this browser
 * dropped the iframe's (partitioned) cookies, so the booking continues on the direct link, top-level, where the
 * cookies are first-party. Never a fallback to tokens in JS storage.
 */
export function CookiesBlockedScreen({ directLink }: { directLink: string }) {
  return (
    <>
      <StateIcon tone="info">
        <ShieldAlert />
      </StateIcon>
      <ScreenTitle>Your browser doesn&apos;t allow booking inside this page.</ScreenTitle>
      <a className="btn btn-primary btn-lg btn-block" href={directLink} target="_blank" rel="noopener">
        Continue in a new tab <ExternalLink aria-hidden />
      </a>
    </>
  )
}
